"use client";
/** Shared opener config: the context <LootProvider> fills and openers read. */
import { createContext, useContext } from "react";
import type { CreateLootClient, Currency, LootApiLike } from "../core/types";
import type { LootLocaleStrings } from "../i18n/strings";
import type { OpenerSoundEventUrls, SoundsProp } from "../sounds/useOpenerSounds";
import type { LootTheme } from "../theme/theme";

/** Connection and theming config every `LootBoxOpener` below inherits. */
export interface LootProviderConfig {
  publishableKey?: string;
  baseUrl?: string;
  sessionToken?: string | null;
  /**
   * True while the host is still resolving the shopper's session (checking who is signed in,
   * minting the token). Openers below show their loading state until it is false, instead of
   * reading the token's absence as demo mode.
   */
  sessionPending?: boolean;
  onSessionExpired?: () => Promise<string | null>;
  /** Advanced: a shared pre-built client instance. */
  client?: LootApiLike;
  /** Advanced: factory overriding how the default client is constructed. */
  createClient?: CreateLootClient;
  theme?: LootTheme;
  sounds?: SoundsProp;
  soundBaseUrl?: string;
  /** The store's sounds by event, above `sounds` / `soundBaseUrl`; battles use them too. */
  soundEvents?: OpenerSoundEventUrls;
  /** The shopper turned sounds off. */
  soundMuted?: boolean;
  /** The shopper's volume, 0 to 100 (a square curve; 0 mutes). Absent plays at full volume. */
  soundVolume?: number;
  /** For the host's own controls: called when the shopper's mute changes. */
  onSoundMutedChange?: (muted: boolean) => void;
  locale?: Partial<LootLocaleStrings>;
  /**
   * How amounts display — box prices, item values, claim totals, all integer US cents on the
   * wire. Match the Storefront → Currency setting in your dashboard (the storefront passes its
   * config's `currency`). Default: credits, `$` with two decimals. Pass a stable reference.
   */
  currency?: Currency;
}

/** Written by <LootProvider>; empty when no provider is mounted. */
export const LootConfigContext = createContext<LootProviderConfig>({});

/** The nearest <LootProvider>'s config (empty object without one). */
export function useLootProviderConfig(): LootProviderConfig {
  return useContext(LootConfigContext);
}
